import { Group, Modal, Stack } from "@mantine/core"
import { IconBold, IconHeading, IconLink, IconBug } from "@tabler/icons-react"
import { AppLanguageMenu, Button, TextInput, ActionIcon } from "@tfg/ui"
import Heading from "@tiptap/extension-heading"
import Link from "@tiptap/extension-link"
import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import React, { useCallback, useEffect, useMemo, useState } from "react"

import { TEXT_CONTENT_TYPE } from "@tfg/types/constants/communication-studio.constant"
import {
  ProductSheetLanguage,
  ProductSheetLanguages,
} from "@tfg/types/constants/language.constants"
import {
  BulletedListContentSchema,
  FormattedTextContentSchema,
  Formatting,
} from "@tfg/types/schemas/communication-studio/components"
import {
  FormattedTextContent,
  BulletedListContent,
  Content,
  ContentBlock,
} from "@tfg/types/types/communication-studio/components"
import { ComponentTypesEnum } from "@tfg/types/types/communication-studio/components.types"

import { useAuth } from "~/store/auth-context"
import { findLanguage, findLanguageFlag } from "~/utils/languages"

import "./TextEditor.css"

export interface DynamicTextEditorProps {
  value?: Content
  onChange: (content: Content) => void
  defaultLanguage?: keyof typeof ProductSheetLanguages
  contentType: "text" | "list"
}

export const DynamicTextEditor: React.FC<DynamicTextEditorProps> = ({
  value = {},
  onChange,
  defaultLanguage = ProductSheetLanguages.EN,
  contentType,
}) => {
  const [selectedLanguage, setSelectedLanguage] =
    useState<ProductSheetLanguage>(defaultLanguage as ProductSheetLanguage)
  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState("")
  const [debugMode, setDebugMode] = useState(false)
  const { isAdmin } = useAuth()
  const currentBlocks = useMemo(
    () => value[selectedLanguage] || [],
    [value, selectedLanguage]
  )

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "editor-link",
        },
      }),
      Heading.configure({
        levels: [3],
      }),
    ],
    content: convertBlocksToHTML(currentBlocks),
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      const blocks = convertHTMLToBlocks(html, contentType)
      const updatedContent = {
        ...value,
        [selectedLanguage]: blocks,
      }
      onChange(updatedContent)
    },
  })
  const activateBulletList = useCallback(() => {
    editor?.chain().focus().toggleBulletList().run()
  }, [editor])

  useEffect(() => {
    if (contentType === "list") {
      activateBulletList()
    }
  }, [contentType, activateBulletList])

  const updateEditorContent = useCallback(() => {
    if (!editor) return
    const html = convertBlocksToHTML(currentBlocks)

    if (editor.getHTML() !== html) {
      const { from, to } = editor.state.selection
      editor.commands.setContent(html)
      editor.commands.setTextSelection({ from, to })
    }
  }, [editor, currentBlocks]) // Add dependencies that should trigger updates

  useEffect(() => {
    updateEditorContent()
  }, [selectedLanguage, updateEditorContent])

  const addLink = () => {
    if (!editor) return

    const { from, to } = editor.state.selection
    const selectedText = editor.state.doc.textBetween(from, to)

    if (!selectedText) {
      alert("Please select text first")
      return
    }

    setLinkModalOpen(true)
  }

  const confirmLink = () => {
    if (!editor || !linkUrl) return

    editor.chain().focus().setLink({ href: linkUrl }).run()
    setLinkModalOpen(false)
    setLinkUrl("")
  }

  const removeLink = () => {
    if (!editor) return
    editor.chain().focus().unsetLink().run()
  }

  const toggleHeading = () => {
    if (!editor) return

    const { from, to, empty } = editor.state.selection

    if (empty) {
      editor.chain().focus().toggleHeading({ level: 3 }).run()
    } else {
      const resolvedFrom = editor.state.doc.resolve(from)

      const blockStart = resolvedFrom.before(resolvedFrom.depth)
      const blockEnd = resolvedFrom.after(resolvedFrom.depth)

      const originalSelection = { from, to }

      editor
        .chain()
        .setTextSelection({ from: blockStart, to: blockEnd })
        .toggleHeading({ level: 3 })
        .setTextSelection(originalSelection)
        .run()
    }
  }

  const isHeadingActive = () => {
    if (!editor) return false

    const { selection } = editor.state
    if (!selection.empty) {
      return false // Don't show as active if text is selected
    }
    if (contentType === "list") return false
    return editor.isActive("heading", { level: 3 })
  }

  if (!editor) {
    return null
  }

  return (
    <Stack>
      {/* Language Selector */}
      <Group justify="space-between" align="center">
        <AppLanguageMenu
          currentLocale={findLanguage(selectedLanguage)}
          handleLanguageChange={async (locale) => {
            setSelectedLanguage(locale as ProductSheetLanguage)
          }}
          languageItems={Object.values(ProductSheetLanguages).map((locale) => ({
            label: findLanguage(locale),
            value: locale,
            icon: findLanguageFlag(locale),
          }))}
        />
        {isAdmin && (
          <Button
            variant={debugMode ? "admin" : "admin-tertiary"}
            size="xs"
            color="violet"
            onClick={() => setDebugMode(!debugMode)}
          >
            <IconBug size={16} /> Debug mode
          </Button>
        )}
      </Group>
      {/* Toolbar */}
      <Group>
        <ActionIcon
          variant={editor.isActive("bold") ? "primary" : "tertiary"}
          onClick={() => editor.chain().focus().toggleBold().run()}
          size={16}
          icon={<IconBold size={16} />}
        />

        <ActionIcon
          variant={editor.isActive("link") ? "primary" : "tertiary"}
          onClick={addLink}
          size={16}
          icon={<IconLink size={16} />}
        />

        {/* Improved Heading Button */}
        <ActionIcon
          variant={isHeadingActive() ? "primary" : "tertiary"}
          onClick={toggleHeading}
          size={16}
          disabled={contentType === "list"} // Disable in list mode
          style={{
            opacity: contentType === "list" ? 0.5 : 1,
            cursor: contentType === "list" ? "not-allowed" : "pointer",
          }}
          icon={<IconHeading size={16} />}
        />

        {editor.isActive("link") && (
          <Button size="xs" variant="tertiary" onClick={removeLink}>
            Remove Link
          </Button>
        )}
      </Group>

      {/* Editor */}
      <div className="editor-container">
        <EditorContent
          editor={editor}
          style={{
            minHeight: "200px",
            border: "1px solid #dee2e6",
            borderRadius: "4px",
            padding: "12px",
          }}
        />
      </div>

      {/* Link Modal */}
      <Modal
        opened={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        title="Add Link"
        size="sm"
      >
        <Stack>
          <TextInput
            label="URL"
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="tertiary" onClick={() => setLinkModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmLink}>Add Link</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Content Preview */}
      {debugMode && (
        <div
          style={{
            marginTop: "20px",
            padding: "12px",
            backgroundColor: "#f8f9fa",
            borderRadius: "4px",
          }}
        >
          <h4
            style={{ margin: "0 0 8px 0", fontSize: "14px", fontWeight: 600 }}
          >
            Content Structure for {ProductSheetLanguages[selectedLanguage]}:
          </h4>
          <pre
            style={{
              fontSize: "11px",
              color: "#666",
              overflow: "auto",
              margin: 0,
              whiteSpace: "pre-wrap",
            }}
          >
            {JSON.stringify(currentBlocks, null, 2)}
          </pre>
        </div>
      )}
    </Stack>
  )
}

// Helper functions to convert between JSON format and HTML
function convertBlocksToHTML(blocks: ContentBlock[]): string {
  if (!blocks || blocks.length === 0) {
    return "<p></p>"
  }

  return blocks
    .map((block) => {
      if (block.type === ComponentTypesEnum.BULLETED_LIST) {
        const listContent = BulletedListContentSchema.safeParse(
          block.textContent
        )
        if (!listContent.success) {
          console.error(listContent.error)
          return "<ul><li></li></ul>"
        }

        return convertBulletedListToHTML(listContent.data)
      } else {
        const textContent = FormattedTextContentSchema.array().safeParse(
          block.textContent
        )
        if (!textContent.success) {
          console.error(textContent.error)
          return "<p></p>"
        }
        return convertFormattedTextToHTML(textContent.data)
      }
    })
    .join("")
}

function convertBulletedListToHTML(content: BulletedListContent): string {
  if (!content?.items?.length) {
    return "<ul><li></li></ul>"
  }

  const listItems = content.items
    .map((item) => {
      let html = item.text

      if (item.formatting?.length) {
        const sortedFormatting = [...item.formatting].sort(
          (a, b) => b.start - a.start
        )

        sortedFormatting.forEach((format) => {
          const before = html.slice(0, format.start)
          const text = html.slice(format.start, format.end)
          const after = html.slice(format.end)

          switch (format.type) {
            case TEXT_CONTENT_TYPE.STRONG:
              html = before + `<strong>${text}</strong>` + after
              break
            case TEXT_CONTENT_TYPE.HYPERLINK:
              html =
                before + `<a href="${format.link || ""}">${text}</a>` + after
              break
          }
        })
      }

      return `<li>${html}</li>`
    })
    .join("")

  return `<ul>${listItems}</ul>`
}

function convertFormattedTextToHTML(content: FormattedTextContent[]): string {
  let html = ""
  for (const item of content) {
    let htmlBlock = item.text

    if (item.formatting?.length) {
      const sortedFormatting = [...item.formatting].sort(
        (a, b) => b.start - a.start
      )

      sortedFormatting.forEach((format) => {
        const before = htmlBlock.slice(0, format.start)
        const text = htmlBlock.slice(format.start, format.end)
        const after = htmlBlock.slice(format.end)

        switch (format.type) {
          case TEXT_CONTENT_TYPE.STRONG:
            htmlBlock = before + `<strong>${text}</strong>` + after
            break
          case TEXT_CONTENT_TYPE.HYPERLINK:
            htmlBlock =
              before + `<a href="${format.link || ""}">${text}</a>` + after
            break
        }
      })
    }

    html += `<${item.tag}>${htmlBlock}</${item.tag}>`
  }

  return html
}

function convertHTMLToBlocks(
  html: string,
  contentType: "text" | "list"
): ContentBlock[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, "text/html")
  const blocks: ContentBlock[] = []

  // Track current text block content
  let currentTextContent: FormattedTextContent[] = []

  const flushCurrentTextBlock = () => {
    if (currentTextContent.length > 0) {
      blocks.push({
        type: ComponentTypesEnum.TEXT_PARAGRAPH_V2,
        textContent: currentTextContent,
      })
      currentTextContent = []
    }
  }

  Array.from(doc.body.childNodes).forEach((node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return
    const el = node as HTMLElement

    if (el.tagName === "UL" && contentType === "list") {
      // Flush any pending text content before adding list
      flushCurrentTextBlock()

      const liElements = Array.from(el.querySelectorAll(":scope > li"))
      const items = liElements.flatMap((li) =>
        parseFormattedTextFromElement(li, "p")
      )
      blocks.push({
        type: ComponentTypesEnum.BULLETED_LIST,
        textContent: { items, style: "disc" },
      })
      return
    }

    if (contentType === "text") {
      if (el.tagName === "H3" || el.tagName === "P") {
        // Add both headings and paragraphs to current text content
        const formatted = parseFormattedTextFromElement(
          el,
          el.tagName.toLowerCase() as "p" | "h3"
        )
        currentTextContent.push(...formatted)
        return
      }
    }
  })

  // Flush any remaining text content
  flushCurrentTextBlock()

  // defaults if empty
  if (blocks.length === 0) {
    blocks.push(
      contentType === "list"
        ? {
            type: ComponentTypesEnum.BULLETED_LIST,
            textContent: {
              items: [{ text: "", tag: "p" as const }],
              style: "disc",
            },
          }
        : {
            type: ComponentTypesEnum.TEXT_PARAGRAPH_V2,
            textContent: [{ text: "", tag: "p" as const }],
          }
    )
  }

  return blocks
}

function parseFormattedTextFromElement(
  element: Element,
  tag: "p" | "h3"
): FormattedTextContent[] {
  const text = element.textContent || ""
  const result: FormattedTextContent[] = []
  const formatting: Formatting = []

  let currentIndex = 0
  const processNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      currentIndex += node.textContent?.length || 0
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element
      const startIndex = currentIndex

      Array.from(el.childNodes).forEach(processNode)

      const endIndex = currentIndex
      const nodeText = el.textContent || ""

      if (el.tagName === "STRONG" && nodeText) {
        formatting.push({
          type: TEXT_CONTENT_TYPE.STRONG,
          start: startIndex,
          end: endIndex,
        })
      } else if (el.tagName === "A" && nodeText) {
        formatting.push({
          type: TEXT_CONTENT_TYPE.HYPERLINK,
          start: startIndex,
          end: endIndex,
          link: el.getAttribute("href") || "",
        })
      }
    }
  }

  Array.from(element.childNodes).forEach(processNode)

  result.push({
    text,
    tag,
    formatting: formatting.length > 0 ? formatting : undefined,
  })
  return result
}
